import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { resourceUpdate } from '../../actions/resources.actions';
import CommentForm from '../../components/dossier-sidebar/comments/CommentForm';
import DossierSidebarContent from '../../components/dossier-sidebar/DossierSidebarContent';
import DossierSidebarNav from '../../components/dossier-sidebar/DossierSidebarNav';
import { APPLICATION_BODY_ID, SIDEBAR_TABS, TOAST_TYPES } from '../../constants/settings.constants';
import { useForm } from '../../hooks/useForm.hook';
import { useTableResource } from '../../hooks/useTableResource.hook';
import { getActivitiesData, getCommentsData } from '../../lib/activity.utils';
import { DOSSIER_ACTIVITY_RESOURCE } from '../../resources/dossier.activity.resource';
import { DOSSIER_COMMENTS_RESOURCE } from '../../resources/dossier.comments.resource';
import { USER_RESOURCE } from '../../resources/user.resource';
import { currentUserSelector } from '../../selectors/auth.selectors';
import { fetchedSelector, fetchErrorSelector } from '../../selectors/fetch.selectors';
import commentsService from '../../services/api/comments.service';
import { activities, comments } from '../../services/bff.api';
import toastService from '../../services/toasts.service';

const resource = DOSSIER_ACTIVITY_RESOURCE;
const CLOSED_WIDTH = 48;
const MIN_WIDTH = 384;
let isResizing = null;

const DossierSidebar = ({ status, involved = [] }) => {
  const dispatch = useDispatch();
  const contentRef = useRef(null);
  const params = useParams();
  const [openedTab, setOpenedTab] = useState(null);
  const isUserFetched = useSelector((state) => fetchedSelector(state, { resource: USER_RESOURCE }));
  const user = useSelector(currentUserSelector);
  const fetchingErrorActivity = useSelector((state) => fetchErrorSelector(state, { resource }));
  const fetchingErrorComments = useSelector((state) =>
    fetchErrorSelector(state, { resource: DOSSIER_COMMENTS_RESOURCE })
  );
  const { data, isLoading: isLoadingActivities } = useTableResource(
    { ...resource, api: { get: () => activities.dossier(params.id) } },
    {
      isFetched: !!params.id && isUserFetched
    }
  );
  const { data: commentsData, isLoading: isLoadingComments } = useTableResource(
    { ...DOSSIER_COMMENTS_RESOURCE, api: { get: () => comments.dossier(params.id) } },
    {
      isFetched: !!params.id && isUserFetched
    }
  );
  const [updatingCommentId, setUpdatingCommentId] = useState('');
  const fetchingError = fetchingErrorActivity || fetchingErrorComments;
  const isLoading = isLoadingActivities || isLoadingComments;
  const {
    data: commentData,
    input,
    submitStart,
    submitOk,
    submitError,
    load,
    isSubmitting
  } = useForm({ text: '', mentions: [] });
  const commentEditForm = useForm({ id: '', text: '', mentions: [] });

  const handleNavClick = (tab) => {
    if (tab === openedTab) {
      setOpenedTab(null);
      contentRef.current.style.width = `${CLOSED_WIDTH}px`;
    } else {
      setOpenedTab(tab);
      if (contentRef.current.offsetWidth < MIN_WIDTH) {
        contentRef.current.style.width = `${MIN_WIDTH}px`;
      }
    }
  };

  const handleCommentAdd = (onSuccess) => {
    submitStart();
    commentsService.add(
      params.id,
      { ...commentData },
      {
        onSuccess: (resp) => {
          submitOk();
          load({ text: '', mentions: [] });
          dispatch(resourceUpdate(DOSSIER_COMMENTS_RESOURCE, 'data', [...commentsData, resp]));
          onSuccess();
        },
        onError: (message) => {
          submitError(message);
          toastService.showToast(TOAST_TYPES.ERROR, message);
        }
      }
    );
  };

  const handleCommentAction = (action, commentId) => {
    switch (action) {
      case 'pin': {
        setUpdatingCommentId(commentId);
        commentsService.pin(params.id, commentId, {
          onError: (message) => {
            setUpdatingCommentId('');
            toastService.showToast(TOAST_TYPES.ERROR, message);
          },
          onSuccess: (resp) => {
            dispatch(
              resourceUpdate(DOSSIER_COMMENTS_RESOURCE, 'data', [
                ...commentsData?.map((comment) => {
                  if (comment.id !== commentId) {
                    return comment;
                  }

                  return resp;
                })
              ])
            );
            setUpdatingCommentId('');
          }
        });
        break;
      }
      case 'unpin': {
        setUpdatingCommentId(commentId);
        commentsService.unpin(params.id, commentId, {
          onError: (message) => {
            setUpdatingCommentId('');
            toastService.showToast(TOAST_TYPES.ERROR, message);
          },
          onSuccess: (resp) => {
            dispatch(
              resourceUpdate(DOSSIER_COMMENTS_RESOURCE, 'data', [
                ...commentsData?.map((comment) => {
                  if (comment.id !== commentId) {
                    return comment;
                  }

                  return resp;
                })
              ])
            );
            setUpdatingCommentId('');
          }
        });
        break;
      }
      case 'delete': {
        setUpdatingCommentId(commentId);
        commentsService.remove(params.id, commentId, {
          onError: (message) => {
            setUpdatingCommentId('');
            toastService.showToast(TOAST_TYPES.ERROR, message);
          },
          onSuccess: (resp) => {
            dispatch(
              resourceUpdate(DOSSIER_COMMENTS_RESOURCE, 'data', [
                ...commentsData?.map((comment) => {
                  if (comment.id !== commentId) {
                    return comment;
                  }

                  return resp;
                })
              ])
            );
            setUpdatingCommentId('');
          }
        });
        break;
      }
      case 'edit': {
        commentEditForm.load(commentsData?.find((c) => c.id === commentId));
        break;
      }

      case 'editSubmit': {
        commentEditForm.submitStart();
        commentsService.update(params.id, commentId, commentEditForm?.data, {
          onError: (message) => commentEditForm.submitError(message),
          onSuccess: (resp) => {
            dispatch(
              resourceUpdate(DOSSIER_COMMENTS_RESOURCE, 'data', [
                ...commentsData?.map((comment) => {
                  if (comment.id !== commentId) {
                    return comment;
                  }

                  return resp;
                })
              ])
            );
            submitOk();
            commentEditForm.load({});
            commentEditForm.reset();
          }
        });
        break;
      }

      case 'editCancel': {
        commentEditForm.load({});
        commentEditForm.reset();
        break;
      }

      default:
        break;
    }
  };

  const handleCollapseClick = () => {
    setOpenedTab(null);
    contentRef.current.style.width = `${CLOSED_WIDTH}px`;
  };

  const handleMousemove = (mouseMoveEvent) => {
    if (isResizing) {
      const clientWidth = document.getElementsByTagName('body')[0].clientWidth;
      const curWidth = clientWidth - mouseMoveEvent.pageX;
      // -17 -> scrollbar width?? will be messed on Mac with 'show scrollbars only when scrolling'
      if (curWidth >= MIN_WIDTH && curWidth <= clientWidth - 17) {
        contentRef.current.style.width = `${curWidth}px`;
      }
    }
  };

  const handleMouseup = () => {
    if (!isResizing) {
      return;
    }
    isResizing = false;
    contentRef.current.style.transition = 'width 150ms ease-in-out';
    document.removeEventListener('mousemove', cbHandleMouseMove);
    document.removeEventListener('mouseup', cbHandleMouseUp);
  };

  const cbHandleMouseMove = useCallback(handleMousemove, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const cbHandleMouseUp = useCallback(handleMouseup, []);

  const startResizing = (e) => {
    e.stopPropagation();
    e.preventDefault();

    contentRef.current.style.transition = 'none';
    document.addEventListener('mousemove', cbHandleMouseMove);
    document.addEventListener('mouseup', cbHandleMouseUp);
    isResizing = true;
  };

  useEffect(() => {
    const handleScroll = (e) => {
      if (e.target.scrollTop < 120) {
        contentRef.current.style.top = `${120 - e.target.scrollTop}px`;
        contentRef.current.style.height = `calc(100% - ${120 - e.target.scrollTop}px)`;
      }
    };
    document.getElementById('js-spo-application-layout')?.addEventListener('scroll', handleScroll);

    return () => {
      document.getElementById(APPLICATION_BODY_ID)?.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div ref={contentRef} className={'spo-o-dossier-sidebar'}>
      <DossierSidebarNav
        openedTab={openedTab}
        onNavClick={handleNavClick}
        onCollapseClick={handleCollapseClick}
        commentsCount={commentsData?.length ?? 0}
      />
      <div className={`spo-o-dossier-sidebar__content-wrapper ${openedTab ? '' : 'collapsed'}`}>
        <DossierSidebarContent
          involved={involved}
          user={user}
          openedTab={openedTab}
          error={fetchingError}
          isLoading={isLoading}
          activities={getActivitiesData(data)}
          comments={getCommentsData(commentsData)}
          onCommentAction={handleCommentAction}
          status={status}
          commentEditForm={commentEditForm}
          updatingCommentId={updatingCommentId}
        />
        {openedTab === SIDEBAR_TABS.COMMENTS && !isLoading && (
          <CommentForm
            text={commentData?.text}
            data={commentData}
            submitting={isSubmitting}
            involved={involved}
            onCommentAdd={handleCommentAdd}
            onInput={(value, field) => input(value, field)}
          />
        )}
      </div>
      {!!openedTab && (
        <div className="drag-handle" onMouseDown={startResizing}>
          <div />
          <div />
        </div>
      )}
    </div>
  );
};

export default DossierSidebar;
